/**
 * `/support` — the two ways of telling us something (PLAN-SUPPORT).
 *
 * One page, two URL tabs, and they are two tabs rather than two pages because
 * they are one errand from the brewer's side: *I want something to be different*.
 * Which half answers it depends on whether anybody else would want the same
 * thing, and that is a question somebody standing on this page can decide by
 * reading the board in front of them.
 *
 * - **Feature requests** (the default, and public). A signed-out visitor reads it
 *   exactly as they read the gallery. It is first because it is the half that
 *   scales: a request that is already on the list needs a vote rather than a
 *   conversation, which is one fewer thread for everybody.
 * - **Your conversations** — the existing `SupportSection`, moved here from
 *   Settings unchanged. It is a private thread with us and it stays one; nothing
 *   about the board touches it.
 *
 * `/settings#support` still works: the settings page turns the legacy fragment
 * into a replace-navigate here, so old bookmarks and every reply notification
 * ever sent still land on the conversation they name.
 *
 * ## Why the section is rendered rather than reimplemented
 *
 * `SupportSection` is self-contained — it brings its own shell, its own data and
 * its own copy, and it returns `null` signed out. It moved house; it did not
 * change. What this page adds is the sentence a signed-out visitor gets in its
 * place, which the section could not have carried while it lived inside a page
 * that already had one.
 */

import type { ReactNode } from 'react';
import { useSession } from '@/auth/session-context';
import { FeatureBoard } from '@/components/support/feature-board';
import { SupportSection } from '@/components/support/support-section';
import { cn } from '@/lib/utils';

/** The tabs, in the order somebody should meet them. */
export const SUPPORT_TABS = [
  { id: 'requests', label: 'Feature requests' },
  { id: 'conversations', label: 'Your conversations' },
] as const;

/** One of the two tab ids. */
export type SupportTab = (typeof SUPPORT_TABS)[number]['id'];

/**
 * The tab a bad or missing URL segment falls back to.
 *
 * The board, not the thread: it is the half that works signed out, so a visitor
 * arriving from anywhere at all gets a page with something on it.
 */
export const DEFAULT_SUPPORT_TAB: SupportTab = 'requests';

/** Narrow an arbitrary URL segment to a tab, or the default. */
export function toSupportTab(value: string | undefined): SupportTab {
  return SUPPORT_TABS.some((tab) => tab.id === value) ? (value as SupportTab) : DEFAULT_SUPPORT_TAB;
}

export interface SupportPageProps {
  readonly tab: SupportTab;
  readonly onTabChange: (tab: SupportTab) => void;
}

export function SupportPage({ tab, onTabChange }: SupportPageProps) {
  const { user } = useSession();

  return (
    <div className="mx-auto flex min-h-full w-full max-w-4xl flex-col gap-4 px-4 py-4 sm:px-8 sm:py-8">
      <header className="flex flex-col gap-1">
        <h1 className="font-semibold text-2xl tracking-tight">Support</h1>
        <p className="max-w-2xl text-muted-foreground text-sm">
          Ask us for help, and vote on what we build next. Requests are public but don't show your
          name. Conversations are private between you and us.
        </p>
      </header>

      <nav aria-label="Support" className="flex gap-1 overflow-x-auto border-border border-b">
        {SUPPORT_TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            aria-current={id === tab ? 'page' : undefined}
            onClick={() => onTabChange(id)}
            className={cn(
              '-mb-px shrink-0 border-b-2 px-3 py-2 font-medium text-sm transition-colors',
              id === tab
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'requests' ? (
        <TabBody note="Requests are public and anonymous. We read every one, and the status beside it is what we have decided so far.">
          <FeatureBoard />
        </TabBody>
      ) : (
        <TabBody note="A conversation here is between you and us. We reply in the app and email you when we do.">
          {user === null ? (
            <p
              data-testid="support-signed-out"
              className="rounded-lg border border-border border-dashed px-4 py-6 text-muted-foreground text-sm"
            >
              Support conversations need an account.{' '}
              <a
                href="/signin"
                className="font-medium text-foreground underline underline-offset-2"
              >
                Sign in
              </a>{' '}
              and yours are waiting.
            </p>
          ) : (
            <SupportSection />
          )}
        </TabBody>
      )}
    </div>
  );
}

/**
 * A tab, with the sentence that says who reads what is in it.
 *
 * Both tabs carry one, including the second, which nobody would misread. A note
 * only on the public tab would read as a warning about the board rather than as
 * the ordinary fact it is, and somebody comparing the two would have to infer the
 * silence — the argument `profiles-page.tsx` makes for the same construction.
 */
function TabBody({ note, children }: { readonly note: string; readonly children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">{note}</p>
      {children}
    </section>
  );
}
